import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { SessionView } from '@wodi/shared';
import { Button, LoadingBlock, Notice } from '@wodi/ui';

import { can } from '../api/session.ts';
import { licenceNoticesQuery, markLicenceNoticeSeen, staffKeys } from '../api/staff.ts';
import { useOnline } from '../hooks/use-online.ts';
import { dayText, errorText, licenceNoun, LoadFailed, plural } from './format.tsx';
import styles from './staff.module.css';

// Home: staff licences (docs/plans/phase2-staff-records.md §1.1, §6), for holders of
// staff.read. The live counts, and the notices the daily check recorded until someone with
// staff.manage marks them seen.

export function licenceCountsText(counts: { expired: number; expiring: number }, days: number) {
  if (counts.expired === 0 && counts.expiring === 0)
    return `No licences have expired or expire within ${String(days)} days.`;
  const parts: string[] = [];
  if (counts.expired > 0)
    parts.push(`${plural(counts.expired, 'licence has', 'licences have')} expired.`);
  if (counts.expiring > 0)
    parts.push(
      `${plural(counts.expiring, 'licence expires', 'licences expire')} within ${String(days)} days.`,
    );
  return parts.join(' ');
}

export function StaffLicencesSection({ session }: { session: SessionView }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const notices = useQuery(licenceNoticesQuery);
  const seen = useMutation({
    mutationFn: markLicenceNoticeSeen,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: staffKeys.notices }),
  });
  if (notices.isPending) return <LoadingBlock label="Loading licence notices" rows={2} />;
  if (notices.isError)
    return (
      <LoadFailed
        error={notices.error}
        fallback="Licence notices couldn't be loaded. Try again."
        onRetry={() => void notices.refetch()}
      />
    );
  const { items, counts, licenceWarningDays } = notices.data;
  const mayMark = can(session, 'staff.manage');
  return (
    <>
      <p>{licenceCountsText(counts, licenceWarningDays)}</p>
      {items.length === 0 ? null : (
        <ul className={styles.list} aria-label="New licence notices">
          {items.map((n) => (
            <li key={n.id} className={styles.item}>
              <span>
                <Link to="/staff/$userId" params={{ userId: n.userId }}>
                  {n.displayName}
                </Link>
                {`: ${licenceNoun(n.licenceBody)} ${n.kind === 'expired' ? 'expired' : 'expires'} on ${dayText(n.licenceExpiresOn)}`}
              </span>
              {mayMark ? (
                <Button
                  dense
                  variant="quiet"
                  isDisabled={!online}
                  isPending={seen.isPending && seen.variables === n.id}
                  pendingLabel="Saving…"
                  onPress={() => {
                    seen.mutate(n.id);
                  }}
                >
                  Mark as seen
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {seen.isError ? (
        <Notice tone="error">{errorText(seen.error, "That wasn't saved. Try again.")}</Notice>
      ) : null}
      <Link to="/staff/licences">See licences to renew</Link>
    </>
  );
}
